import React, { useState } from 'react';
import { ArrowRight, Check, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { countryCodes } from '../data/mockData';

const HeroBanner = () => {
  const { toast } = useToast();
  const hero = useScrollAnimation({ threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: '', email: '', countryCode: '+1', phone: '', description: ''
  });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailChange = (value) => {
    setFormData({ ...formData, email: value });
    setErrors({ ...errors, email: value && !validateEmail(value) ? 'Please enter a valid email address' : '' });
  };

  const handlePhoneChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length <= 10) {
      setFormData({ ...formData, phone: digitsOnly });
      setErrors({ ...errors, phone: digitsOnly && digitsOnly.length !== 10 ? 'Phone number must be exactly 10 digits' : '' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = { email: '', phone: '' };
    let hasErrors = false;
    if (!validateEmail(formData.email)) { newErrors.email = 'Please enter a valid email address'; hasErrors = true; }
    if (formData.phone.length !== 10) { newErrors.phone = 'Phone number must be exactly 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); return; }
    toast({ title: "Request Received!", description: "Our trademark experts will contact you within 24 hours." });
    setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
    setErrors({ email: '', phone: '' });
  };

  return (
    <section id="home" data-testid="hero-section" ref={hero.ref} className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden bg-[#030308]">
      {/* Background */}
      <div className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1741356474349-3ca53f0601ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWJzdHJhY3QlMjBnb2xkZW4lMjBsaW5lcyUyMGx1eHVyeSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc1MjQ2OTA5fDA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#030308]/80" />
      </div>
      <div className="absolute bottom-0 left-0 w-[50%] h-[40%] bg-[radial-gradient(ellipse_at_20%_90%,rgba(99,102,241,0.12),transparent_60%)]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10 w-full">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
          {/* Left */}
          <div className={`lg:col-span-3 space-y-8 order-2 lg:order-1 scroll-slide-left ${hero.isVisible ? 'scroll-visible' : ''}`}>
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-md px-4 py-1.5 mb-6">
                <Zap className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-xs tracking-[0.15em] uppercase text-amber-500 font-bold">Limited Time: Filing Starts at $45</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[4.5rem] font-black tracking-tighter text-white leading-[0.95] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Your Brand Is
                <span className="block text-gradient-amber">Unprotected.</span>
                <span className="block text-slate-400 text-3xl sm:text-4xl lg:text-5xl mt-2 normal-case font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Fix that today.</span>
              </h1>
            </div>

            <p className="text-base text-slate-300 leading-relaxed max-w-xl">
              Every day without a registered trademark is another day someone can steal your brand name, copy your products, and profit from your work. <span className="text-white">Trademark My LLC</span> files your USPTO application fast, fights for approval, and gets you protected.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                'Federal protection across all 50 states',
                'Filed by licensed trademark attorneys',
                'Amazon Brand Registry enrollment',
                'Office action response included'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 mt-0.5 rounded-sm bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-amber-500" />
                  </div>
                  <span className="text-sm text-slate-400">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div className={`lg:col-span-2 order-1 lg:order-2 scroll-slide-right scroll-delay-2 ${hero.isVisible ? 'scroll-visible' : ''}`}>
            <div data-testid="hero-contact-form" className="backdrop-blur-2xl bg-black/40 border border-white/10 rounded-xl shadow-2xl p-7">
              <div className="text-center mb-6">
                <span className="inline-block bg-gradient-to-r from-amber-500 to-orange-600 text-white px-4 py-1 rounded-md text-xs font-bold mb-3 uppercase tracking-wider">
                  Free Consultation
                </span>
                <h3 className="text-lg font-bold tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Protect Your Brand Now
                </h3>
                <p className="text-sm text-slate-400 mt-1">Speak with a trademark attorney within 24 hours</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3.5">
                <div className="space-y-1.5">
                  <Label htmlFor="hero-name" className="text-slate-400 text-sm">Full Name *</Label>
                  <Input id="hero-name" data-testid="hero-name-input" placeholder="Enter your full name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="h-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg" />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="hero-email" className="text-slate-400 text-sm">Email *</Label>
                  <Input id="hero-email" data-testid="hero-email-input" type="email" placeholder="your@email.com" value={formData.email} onChange={(e) => handleEmailChange(e.target.value)} required className={`h-10 bg-white/5 text-white placeholder:text-slate-600 rounded-lg ${errors.email ? 'border-red-500' : 'border-white/10'} focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500`} />
                  {errors.email && <p className="text-red-400 text-xs">{errors.email}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="hero-phone" className="text-slate-400 text-sm">Phone *</Label>
                  <div className="flex gap-2">
                    <Select value={formData.countryCode} onValueChange={(v) => setFormData({ ...formData, countryCode: v })}>
                      <SelectTrigger data-testid="hero-country-select" className="w-[110px] h-10 bg-white/5 border-white/10 text-white focus:border-indigo-500 rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px] bg-[#0A0A12] border-white/10">
                        {countryCodes.map((item) => (
                          <SelectItem key={item.code} value={item.code} className="text-white hover:bg-white/5 focus:bg-white/5 focus:text-white">
                            <span className="flex items-center gap-2"><span>{item.flag}</span><span>{item.code}</span></span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input id="hero-phone" data-testid="hero-phone-input" type="tel" placeholder="1234567890" value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)} required maxLength={10} className={`flex-1 h-10 bg-white/5 text-white placeholder:text-slate-600 rounded-lg ${errors.phone ? 'border-red-500' : 'border-white/10'} focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500`} />
                  </div>
                  {errors.phone && <p className="text-red-400 text-xs">{errors.phone}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="hero-desc" className="text-slate-400 text-sm">What do you need?</Label>
                  <Textarea id="hero-desc" data-testid="hero-description-input" placeholder="e.g. Register my brand for Amazon Brand Registry" rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none rounded-lg" />
                </div>

                <Button type="submit" data-testid="hero-contact-submit" className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white h-11 text-base font-bold rounded-md uppercase tracking-wider btn-shine shadow-lg shadow-amber-500/20">
                  Get My Free Consultation
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>

                <p className="text-[11px] text-slate-600 text-center">By submitting, you agree to our Terms of Service and Privacy Policy.</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
